/**
 * Content Control operations for negotiation comments.
 *
 * Inserts, reads, removes, and navigates to Content Controls that
 * anchor comments to specific text in the Word document.
 *
 * CC tag format: `kobsign:comment:{uuid12}` (matches backend model).
 * Pattern follows useSignatureControls.ts.
 */

import { devLog } from '../services/devLog'

const COMMENT_CC_PREFIX = 'kobsign:comment:'

/** Insert a Content Control around the current selection to anchor a comment */
export async function insertCommentControl(
  tag: string,
  anchorText: string,
  colorHex: string,
): Promise<void> {
  await Word.run(async (context) => {
    const selection = context.document.getSelection()
    selection.load('text')
    await context.sync()

    // If no text is selected, insert the anchor text as a marker
    let targetRange: Word.Range
    if (!selection.text || selection.text.trim() === '') {
      targetRange = selection.insertText(anchorText, Word.InsertLocation.replace)
    } else {
      targetRange = selection
    }

    const cc = targetRange.insertContentControl()
    cc.tag = tag
    cc.title = '' // Keep clean — comment text lives in the panel

    try { cc.color = colorHex } catch { /* WordApi 1.3+ */ }
    try { cc.appearance = Word.ContentControlAppearance.boundingBox } catch { /* best-effort */ }
    try { cc.cannotDelete = true } catch { /* best-effort */ }

    await context.sync()

    // Move cursor after the CC
    const afterRange = cc.getRange(Word.RangeLocation.after)
    afterRange.select(Word.SelectionMode.end)
    await context.sync()

    devLog.info('CommentCC', `Inserted CC: ${tag}`)
  })
}

/** Read all comment Content Controls in the document */
export async function readCommentControls(): Promise<Array<{ tag: string; text: string }>> {
  return await Word.run(async (context) => {
    const controls = context.document.body.contentControls
    controls.load('items/tag,items/text')
    await context.sync()

    const results: Array<{ tag: string; text: string }> = []
    for (const cc of controls.items) {
      if (!cc.tag?.startsWith(COMMENT_CC_PREFIX)) continue
      results.push({ tag: cc.tag, text: cc.text || '' })
    }

    devLog.info('CommentCC', `Read ${results.length} comment CC(s)`)
    return results
  })
}

/** Remove a comment Content Control by tag */
export async function removeCommentControl(tag: string): Promise<void> {
  await Word.run(async (context) => {
    const controls = context.document.body.contentControls
    controls.load('items/tag')
    await context.sync()

    for (const cc of controls.items) {
      if (cc.tag === tag) {
        try { cc.cannotDelete = false } catch { /* best-effort */ }
        cc.delete(true) // true = keep content, remove CC wrapper
      }
    }
    await context.sync()
    devLog.info('CommentCC', `Removed CC: ${tag}`)
  })
}

/** Scroll Word to a comment's Content Control */
export async function scrollToCommentControl(tag: string): Promise<boolean> {
  if (!tag) return false

  try {
    return await Word.run(async (context) => {
      const controls = context.document.body.contentControls
      controls.load('items/tag')
      await context.sync()

      for (const cc of controls.items) {
        if (cc.tag === tag) {
          cc.select(Word.SelectionMode.select)
          await context.sync()
          devLog.info('CommentCC', `Scrolled to CC: ${tag}`)
          return true
        }
      }

      devLog.warn('CommentCC', `CC not found: ${tag}`)
      return false
    })
  } catch (e) {
    devLog.warn('CommentCC', `Scroll failed: ${e}`)
    return false
  }
}

/**
 * Scroll to a comment by trying CC first, then anchor text search,
 * then paragraph index as final fallback.
 */
export async function scrollToComment(
  tag: string | undefined,
  anchorText: string | undefined,
  paragraphIndex: number | null | undefined,
): Promise<boolean> {
  // Strategy 1: Content Control
  if (tag) {
    const found = await scrollToCommentControl(tag)
    if (found) return true
  }

  // Strategy 2: Search for anchor text in document
  if (anchorText && anchorText.length > 3) {
    try {
      return await Word.run(async (context) => {
        const results = context.document.body.search(anchorText, {
          matchCase: false,
          matchWholeWord: false,
        })
        results.load('items')
        await context.sync()

        if (results.items.length > 0) {
          results.items[0].select(Word.SelectionMode.select)
          await context.sync()
          devLog.info('CommentCC', `Scrolled to anchor text: "${anchorText.substring(0, 30)}..."`)
          return true
        }
        return false
      })
    } catch (e) {
      devLog.warn('CommentCC', `Anchor text search failed: ${e}`)
    }
  }

  // Strategy 3: Navigate to paragraph by index
  if (paragraphIndex != null && paragraphIndex >= 0) {
    try {
      return await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs
        paragraphs.load('items')
        await context.sync()

        if (paragraphIndex < paragraphs.items.length) {
          paragraphs.items[paragraphIndex].select(Word.SelectionMode.start)
          await context.sync()
          devLog.info('CommentCC', `Scrolled to paragraph ${paragraphIndex}`)
          return true
        }
        return false
      })
    } catch (e) {
      devLog.warn('CommentCC', `Paragraph scroll failed: ${e}`)
    }
  }

  return false
}

/** Helper: search for text in the document and create a CC if found */
async function searchAndCreate(
  context: Word.RequestContext,
  body: Word.Body,
  searchText: string,
  comment: { word_cc_tag: string; visibility: string },
  getColor: (c: { visibility: string }) => string,
): Promise<boolean> {
  const searchResults = body.search(searchText, {
    matchCase: false,
    matchWholeWord: false,
  })
  searchResults.load('items')
  await context.sync()

  if (searchResults.items.length === 0) return false

  const range = searchResults.items[0]
  const cc = range.insertContentControl()
  cc.tag = comment.word_cc_tag
  cc.title = ''
  const color = getColor(comment)
  try { cc.color = color } catch { /* WordApi 1.3+ */ }
  try { cc.appearance = Word.ContentControlAppearance.boundingBox } catch { /* best-effort */ }
  try { cc.cannotDelete = true } catch { /* best-effort */ }
  await context.sync()
  devLog.info('CommentCC', `Auto-created CC for "${searchText.substring(0, 30)}..." tag=${comment.word_cc_tag}`)
  return true
}

/**
 * Sync missing Content Controls for comments that have anchors but no CC in the document.
 * Searches the document text for each anchor and creates a CC if found.
 * Uses fallback strategies: exact → collapsed whitespace → longest word substring.
 */
export async function syncMissingContentControls(
  comments: Array<{
    word_cc_tag: string
    anchors: Array<{ anchor_text: string; anchor_paragraph: number | null }>
    visibility: string
  }>,
  existingTags: Set<string>,
  getColor: (comment: { visibility: string }) => string,
): Promise<number> {
  let created = 0

  for (const comment of comments) {
    if (!comment.word_cc_tag) continue
    if (existingTags.has(comment.word_cc_tag)) continue
    const anchor = comment.anchors?.[0]
    if (!anchor?.anchor_text) continue

    try {
      await Word.run(async (context) => {
        const body = context.document.body
        const text = anchor.anchor_text

        // Strategy 1: exact search
        let found = await searchAndCreate(context, body, text, comment, getColor)

        // Strategy 2: collapse whitespace (PDF text may differ from Word)
        if (!found) {
          const collapsed = text.replace(/\s+/g, ' ').trim()
          if (collapsed !== text) {
            found = await searchAndCreate(context, body, collapsed, comment, getColor)
          }
        }

        // Strategy 3: search for longest word (>4 chars) as substring match
        if (!found && text.length > 6) {
          const words = text.split(/\s+/).filter(w => w.length > 4)
          if (words.length > 0) {
            const longest = words.reduce((a, b) => a.length >= b.length ? a : b)
            devLog.info('CommentCC', `Trying substring search: "${longest}"`)
            found = await searchAndCreate(context, body, longest, comment, getColor)
          }
        }

        if (found) {
          created++
        } else {
          devLog.warn('CommentCC', `Text not found in doc: "${text.substring(0, 40)}"`)
        }
      })
    } catch (e) {
      devLog.warn('CommentCC', `Failed to create CC: ${e}`)
    }
  }

  return created
}

/** Get the current selection text and paragraph index for anchoring a new comment */
export async function getSelectedTextForAnchor(): Promise<{
  text: string
  paragraphIndex: number
} | null> {
  try {
    return await Word.run(async (context) => {
      const selection = context.document.getSelection()
      selection.load('text')
      await context.sync()

      // Clean selection text: remove Word control chars (\r, \v, \x0B, non-breaking spaces)
      // then trim whitespace. Word often includes trailing paragraph marks and soft hyphens.
      const raw = selection.text ?? ''
      const text = raw.replace(/[\r\v\x0B\u00AD\u200B]/g, '').replace(/\u00A0/g, ' ').trim()
      if (!text) return null

      // Get paragraph index — use text comparison as fallback for Word versions without uniqueLocalId
      const selParagraphs = selection.paragraphs
      selParagraphs.load('items/text')
      const bodyParagraphs = context.document.body.paragraphs
      bodyParagraphs.load('items/text')
      await context.sync()

      let paragraphIndex = 0
      if (selParagraphs.items.length > 0) {
        const selParaText = selParagraphs.items[0].text

        // Try uniqueLocalId first (WordApi 1.5+), fall back to text matching
        try {
          selParagraphs.items[0].load('uniqueLocalId')
          for (const bp of bodyParagraphs.items) {
            bp.load('uniqueLocalId')
          }
          await context.sync()

          for (let i = 0; i < bodyParagraphs.items.length; i++) {
            if (bodyParagraphs.items[i].uniqueLocalId === selParagraphs.items[0].uniqueLocalId) {
              paragraphIndex = i
              break
            }
          }
        } catch {
          // Fallback: match by paragraph text content
          for (let i = 0; i < bodyParagraphs.items.length; i++) {
            if (bodyParagraphs.items[i].text === selParaText) {
              paragraphIndex = i
              break
            }
          }
        }
      }

      devLog.info('CommentCC', `Selected anchor: "${text.substring(0, 30)}..." para=${paragraphIndex}`)
      return { text, paragraphIndex }
    })
  } catch (e) {
    devLog.warn('CommentCC', `getSelectedTextForAnchor failed: ${e}`)
    return null
  }
}
